'use client';

import { useEffect, useState } from 'react';
import { useTranslation } from 'next-i18next';
import { Avatar, ImageBj } from '@components/common';
import SvgIcon from '@images/premium/badge-premium-talent.svg';
import PremiumBg from '@images/premium/img-highlight-premium.svg';
import { showTalentSubscriptionModal } from '@components/templates/talent/subscription/TalentSubscriptionModal';
import { useExtendedSearchParams } from '@hooks/next-routing-wrappers';
import { useAppSelector } from '@store/index';
import { getUserHeaderInfo } from '@store/reducers/userSelector';
import maleAvatar from '@images/avatars/img-profile-pic-placeholder-1.png';
import { ApiUserSubscriptionPaymentDetailsOk } from '@type/v1-api-types';
import { useHandleNotAuthOrder } from '@components/templates/employer/subscription/utils/useHandleNotAuthOrder';

// Interface
// *****************************************************
interface PremiumHeroProps {
  locale: Locale,
  isAuth?: boolean,
  paymentDetails?: ApiUserSubscriptionPaymentDetailsOk,
}

/**
 * @description Hero section for Premium page. It can be used for: user has an active subscription,
 * user not subscribed, subscription expired or canceled, user not logged in.
 *
 * * **locale** - the current page locale.
 */
export const TalentPremiumHero = (props: PremiumHeroProps) => {
  // Destructure props
  const {
    locale,
    paymentDetails,
    isAuth,
  } = props;

  const { searchParams } = useExtendedSearchParams();

  const openedPaymentModal = searchParams?.get('openedPaymentModal');

  const [openPaymentModal, setOpenPaymentModal] = useState<boolean>(false);

  // Handle trigger the upgrade payment modal for logged-in users if there's a subscription order
  // saved in session storage.
  // *****************************************************
  const { isLoggedIn, tryToTriggerUpgrade } = useHandleNotAuthOrder(locale);

  // Translation
  const { t } = useTranslation('common');

  // Helper variables
  const headerInfo = useAppSelector(getUserHeaderInfo);
  const avatar = isAuth ? headerInfo?.profileImg : maleAvatar.src;
  const imgFolder = isAuth ? 'resume_image' : 'next';
  const extractImageName = !!isAuth;
  const name = headerInfo?.name || '';

  // Trigger payment modal on mount if the user is logged-in
  useEffect(() => {
    if (isLoggedIn) tryToTriggerUpgrade();

    // Perform checks before triggering modal show
    if (openedPaymentModal === 'true') {
      if (isLoggedIn && headerInfo && headerInfo.role === 'USER') {
        setOpenPaymentModal(true);
      }
    }

    if (openPaymentModal) {
      showTalentSubscriptionModal(locale, paymentDetails);
    }
  }, [headerInfo, isLoggedIn, openPaymentModal, openedPaymentModal, tryToTriggerUpgrade, paymentDetails, locale]);


  // Render component
  // *****************************************************
  return (
    <div className="container">
      <div className="mb-4 grid md:mx-8 md:grid-cols-2 lg:mx-32">
        <div className="grid">
          <div className="relative flex min-h-60 flex-wrap items-start justify-center pt-11 md:min-h-0 lg:-mx-32">
            <ImageBj
              src={PremiumBg as string}
              alt="Premium background"
              className="absolute top-0 h-[231px] object-cover"
            />
            <div className="relative">
              <ImageBj
                src={SvgIcon as string}
                alt="Premium Badge"
                className="absolute left-1/2 top-1/2 size-44 -translate-x-1/2 -translate-y-1/2 text-white"
              />
              <Avatar
                size="xl"
                imgPath={avatar}
                imgFolder={imgFolder}
                extractImageName={extractImageName}
                name={name}
              />
            </div>
          </div>
          <div className="my-5 items-baseline">
            <h1 className="mb-4 text-4xl font-extrabold text-dark">{t('premium.talent.hero.heading')}</h1>
            <div className="text-dark">
              <p>{t('premium.talent.hero.intro')}</p>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};
